/**
 * VideoIconProgress Component
 *
 * Displays an icon and progress for a video lesson, with a link to the video resource.
 * The icon and link adapt based on user access rights and video progress state.
 *
 * Features:
 * - Dynamic link generation for video resource
 * - Conditional rendering of progress icon and link based on access rights
 * - Multi-language support via Tooltip
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Data object containing video info (idCourse, idVideo, title, desc)
 * @param {boolean} props.accessCourse - Whether the user has access to the course
 * @param {Array} props.userTrack - Array of user tracking objects for video progress
 * @returns {JSX.Element} The rendered video icon and progress
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import { getLinkLtik } from 'constants/LTIData';
import CourseProgress from 'components/UI/CourseProgress/CourseProgress';
import classes from './VideoIconProgress.module.css';

const url = window.location.pathname.split('/');

const VideoIconProgress = props => {
  const [activityState, setActivityState] = useState(
    'without_starting'
  );

  useEffect(() => {
    // Set video progress state based on userTrack
    props.userTrack.forEach(track => {
      if (track.id_media === props.data.idVideo) {
        if (track.media_play === 2) setActivityState('finished');
        else if (track.media_play === 1) setActivityState('started');
        else if (track.media_play === 0)
          setActivityState('without_starting');
      }
    });
  }, [props.userTrack]);

  return (
    <>
      <Tooltip placement="bottom" title="Video">
        <Link
          to={
            getLinkLtik()
              ? `/f/${props.data.idCourse}/${
                  props.data.idVideo
                }/?ltik=${getLinkLtik()}`
              : url[2] === 'a'
              ? `/a/f/${props.data.idCourse}/${props.data.idVideo}`
              : url[2] === 'course'
              ? `/course/f/${props.data.idCourse}/${props.data.idVideo}`
              : `/f/${props.data.idCourse}/${props.data.idVideo}`
          }
          className={`${classes.productLesson} ${
            !props.accessCourse ? classes.productLessonDisabled : ''
          }`}
        >
          <CourseProgress
            activityState={activityState} // Prop passed: video progress state
            icon="video" // Prop passed: icon type for video
          />
        </Link>
      </Tooltip>
      <p className={classes.productContentItemText}>
        {props.data.title}
        {/* Prop passed: video title */}
      </p>
      <p className={classes.descriptionText}>
        {props.data.desc}
        {/* Prop passed: video description */}
      </p>
    </>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
VideoIconProgress.propTypes = {
  /**
   * Data object containing video info (idCourse, idVideo, title, desc)
   * Used to generate links and display video details
   * Passed as prop to Link, CourseProgress, and text elements
   */
  data: PropTypes.shape({
    idCourse: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    idVideo: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    title: PropTypes.string,
    desc: PropTypes.string
  }),
  /**
   * Boolean indicating whether the user has access to the course
   * Controls link and icon rendering/styling
   * Passed as prop to Link and CourseProgress
   */
  accessCourse: PropTypes.bool,
  /**
   * Array of user tracking objects for video progress
   * Used to determine video progress state
   * Passed as prop to internal logic
   */
  userTrack: PropTypes.arrayOf(PropTypes.object)
};

VideoIconProgress.defaultProps = {
  data: {
    idCourse: '',
    idVideo: '',
    title: '',
    desc: ''
  },
  accessCourse: false,
  userTrack: []
};

export default VideoIconProgress;
